import React, { useState, useEffect } from 'react'
import { useGameplayContext } from '../../contexts/gameplayContext'
import { MetricCard } from '../shared/MetricCard'
import {
  Clock,
  ArrowUp,
  Trophy,
  CirclePercent,
  HelpCircle,
  Wind,
  Users,
  Skull,
  Timer,
  Waves,
  X,
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsRight
} from 'lucide-react'
import { formatUnderscoreCase, formatDuration, formatCurrency } from '../../utils'
import { MIN_ENERGY_FOR_ACTIVITY_START } from '../../constants/game'
import { Tooltip, Select, SelectItem, Checkbox } from '@nextui-org/react'
import { GamePlayData, SkillEnum, SkillResult, WaveResult } from '@/types'
import SkillShortcut from '../shared/SkillShortcut'
import { SkillPill } from '../shared/SkillPill'
import LOCATIONS from '@/constants/locations'
import { SURF_SPOTS } from '@/constants/surfSpots'

type ColorCodedValue = {
  index: number
  colorCode: string
}

type SurfConditionMap = {
  [key: string]: ColorCodedValue
}

type IndexedWaveResult = WaveResult & { id: number }
const generateColorCode = (index: number, total: number): string => {
  const hue = 120 - (120 * index) / (total - 1)
  return `hsl(${hue}, 70%, 35%)`
}

const generateConditionMap = (conditionObj: object): SurfConditionMap => {
  const keys = Object.keys(conditionObj)
  return keys.reduce((acc, key, index) => {
    acc[key] = {
      index,
      colorCode: generateColorCode(index, keys.length)
    }
    return acc
  }, {} as SurfConditionMap)
}

export const WAVE_HEIGHT_MAP: SurfConditionMap = generateConditionMap(LOCATIONS.hawaii.waveHeight)

export const WAVE_TYPE_MAP: SurfConditionMap = generateConditionMap(LOCATIONS.hawaii.waveType)

export const CROWD_MAP: SurfConditionMap = generateConditionMap(LOCATIONS.hawaii.crowd)

export const CROWD_SKILL_MAP: SurfConditionMap = generateConditionMap(LOCATIONS.hawaii.crowdSkill)

export const WAVE_SPEED_MAP: SurfConditionMap = generateConditionMap(LOCATIONS.hawaii.waveSpeed)

export const WAVE_TIME_MAP: SurfConditionMap = generateConditionMap(LOCATIONS.hawaii.waveTime)

const getStyledConditionText = (condition: string, map: SurfConditionMap) => {
  return (
    <span
      className="text-white px-2 py-0.5 rounded-full text-sm inline-flex items-center"
      style={{
        backgroundColor: map[condition]?.colorCode || '#666666',
        textShadow: '0 1px 2px rgba(0,0,0,0.3)'
      }}
    >
      {formatUnderscoreCase(condition)}
    </span>
  )
}

const getWaveTimeColor = (seconds: number): string => {
  const hue = Math.min(120, (120 * seconds) / 15) // Max green at 15+ seconds
  return `hsl(${hue}, 70%, 35%)`
}

const getWaitingTimeColor = (minutes: number): string => {
  const hue = Math.max(0, 120 - (120 * minutes) / 15) // Start green, red at 15+ minutes
  return `hsl(${hue}, 70%, 35%)`
}

const getCatchProbabilityColor = (probability: number): string => {
  // Scale from 0.2 (20%) to 0.8 (80%)
  const hue = Math.max(0, Math.min(120, (120 * (probability - 0.2)) / 0.6))
  return `hsl(${hue}, 70%, 35%)`
}

export const SurfScreen: React.FC = () => {
  const { gameData, setGameData } = useGameplayContext()
  const { surfSession, currencies, currentActivity, equipment } = gameData
  const currentLocation = LOCATIONS[gameData.currentLocation]
  const currentSpot = gameData.currentLocationSpot
  const [isTooltipOpen, setIsTooltipOpen] = useState(false)
  const [reverseSessionData, setReverseSessionData] = useState<IndexedWaveResult[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  useEffect(() => {
    // Create new array with IDs in reverse order (newest first)
    const indexedWaves = surfSession.appliedSessionData
      .map((wave, index) => ({
        ...wave,
        id: index + 1 // First item will be length, last will be 1
      }))
      .reverse()

    setReverseSessionData(indexedWaves)
    // Reset to first page when session data changes
    setCurrentPage(1)
  }, [surfSession.appliedSessionData])

  // Calculate pagination
  const totalPages = Math.max(1, Math.ceil(reverseSessionData.length / itemsPerPage))
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentWaves = reverseSessionData.slice(startIndex, endIndex)

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items)
    setCurrentPage(1)
  }

  const canStartSurfing =
    currencies.energy.amount >= MIN_ENERGY_FOR_ACTIVITY_START &&
    currencies.money.amount >= currentLocation.surfSessionCost &&
    !!equipment.surfboard

  const handleSurfing = () => {
    if (currentActivity === 'surf') {
      setGameData((prev) => ({
        ...prev,
        currentActivity: 'rest'
      }))
    } else {
      setGameData((prev) => ({
        ...prev,
        currentActivity: 'surf'
      }))
    }
  }

  const tooltipContent = (
    <div className="max-w-md space-y-1 p-2">
      <p className="pt-2 pb-2 border-b border-gray-300">
        This is where you go surfing! Each session costs money (reflecting living, food, and travel expenses in your
        current location) and requires energy.
      </p>
      <p className="pt-2 pb-2 border-b border-gray-300">
        🏆 Each surfing session earns skill points which improve your skill levels. Critical maneuvers earn extra points
        and 👏&nbsp;Respect. Critical probability is{' '}
        <b>
          {(currencies.crit_probability.amount * (currencies.crit_probability.totalMultiplier || 1) * 100).toFixed(1)}%
        </b>{' '}
        and multiplier is{' '}
        <b>{(currencies.crit_multiplier.amount * (currencies.crit_multiplier.totalMultiplier || 1)).toFixed(1)}x</b>.
        Both can be improved by finishing story quests.
      </p>
      <p className="pt-2 pb-2 border-b border-gray-300">
        💵 Cost for current location {currentLocation.name}: ${currentLocation.surfSessionCost}
      </p>
      <p className="pt-2 pb-2 border-b border-gray-300">
        ⚡ Energy allows 1 hour of surfing per 20 points. You can replenish it by resting. Min. energy to start a
        session: {MIN_ENERGY_FOR_ACTIVITY_START}
      </p>
      <p className="pt-2 pb-2 border-b border-gray-300">
        💪 Increase your Fitness level to unlock higher energy limits!
      </p>
      <p className="pt-2 pb-2 border-b border-gray-300">
        🌊 Surf conditions like wave type, height, speed and crowd levels are based on your current location. Your catch
        probability depends on these conditions and is improved by skills like Fitness, Paddling, Wave Reading and
        Forecasting & Observing.
      </p>
      <p className="pt-2 pb-2 border-b border-gray-300">
        Points calculation: Random(10) × Overall skill level multiplier × Skill effects multiplier × Critical multiplier
        (if critical)
      </p>
      <p className="pt-2 pb-2">
        <Wind className="w-4 h-4 inline mr-1" /> Wave Speed: How fast the wave is moving, affects difficulty and scoring
        •
        <Clock className="w-4 h-4 inline mx-1" /> Wave Time: Duration of the wave ride, determines how many maneuvers
        you can perform •
        <Timer className="w-4 h-4 inline mx-1" /> Waiting Time: Time between waves, affects session length and wave
        count •
        <CirclePercent className="w-4 h-4 inline mx-1" /> Catch Probability: Your chance to catch the wave, based on
        conditions and skills •
        <Trophy className="w-4 h-4 inline mx-1" /> Total Points: Sum of all skill points earned from maneuvers •
        <span className="text-lg inline mx-1">👏</span> Respect: Earned from critical maneuvers, improves your
        reputation
      </p>
    </div>
  )

  const getRequirementsMessage = () => {
    const needsEnergy = currencies.energy.amount < MIN_ENERGY_FOR_ACTIVITY_START
    const needsMoney = currencies.money.amount < currentLocation.surfSessionCost

    if (needsEnergy && needsMoney) {
      return `Need more energy and $${formatCurrency(
        currentLocation.surfSessionCost - currencies.money.amount
      )} more money`
    } else if (needsEnergy) {
      return `Need ${formatCurrency(MIN_ENERGY_FOR_ACTIVITY_START - currencies.energy.amount)} more energy`
    } else if (needsMoney) {
      return `Need $${formatCurrency(currentLocation.surfSessionCost - currencies.money.amount)} more money`
    } else if (!equipment.surfboard) {
      return `Need to equip a surfboard`
    }
    return ''
  }

  const handleWaveTypeChange = (value: string) => {
    setGameData((prev) => ({
      ...prev,
      isSurfingGreenWave: value === 'green_wave'
    }))
  }

  const handleCatchStokeChange = (isSelected: boolean) => {
    setGameData((prev) => ({
      ...prev,
      catchStoke: isSelected
    }))
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <h2 className="text-3xl font-bold text-white drop-shadow-md">🌊 Surf</h2>
        <Tooltip
          content={tooltipContent}
          isOpen={isTooltipOpen}
          onOpenChange={(open) => setIsTooltipOpen(open)}
          className="bg-white  backdrop-blur-sm text-sm"
          placement="bottom"
        >
          <button
            className="text-white hover:text-blue-300 transition-colors"
            onClick={() => setIsTooltipOpen(!isTooltipOpen)}
          >
            <HelpCircle className="w-6 h-6" />
          </button>
        </Tooltip>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          emoji="⚡"
          label="Energy"
          value={formatCurrency(currencies.energy.amount)}
          max={currencies.energy.max}
        />

        <MetricCard emoji="🏆" label="Skill Points" value={formatCurrency(currencies.skill.amount)} />

        <MetricCard
          emoji="📍"
          label="Surf Spot"
          value={
            <div className="flex flex-col gap-1">
              <span>{currentSpot}</span>
              <span className="text-sm text-gray-500">{currentLocation.name}</span>
              <div className="flex flex-wrap gap-1">
                {SURF_SPOTS[currentSpot].map((multiplier) => (
                  <SkillPill key={multiplier.key} skill={multiplier.key as SkillEnum} value={multiplier.value} />
                ))}
              </div>
            </div>
          }
          className="sm:col-span-2"
        />
      </div>

      <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-center sm:space-x-4">
        <Tooltip
          content={!canStartSurfing && currentActivity !== 'surf' ? getRequirementsMessage() : ''}
          className="bg-white bg-opacity-80 backdrop-blur-sm text-sm"
          placement="bottom"
          isDisabled={canStartSurfing || currentActivity === 'surf'}
        >
          <button
            className="w-full sm:w-auto bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-6 rounded-lg text-xl transition-colors inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleSurfing}
            disabled={!canStartSurfing && currentActivity !== 'surf'}
          >
            <Waves className="w-6 h-6 mr-2" />
            {currentActivity === 'surf' ? 'Stop Surfing' : `Go Surfing ($${currentLocation.surfSessionCost})`}
          </button>
        </Tooltip>

        <div className="flex flex-row items-center justify-between w-full sm:w-auto space-x-3">
          <Select
            selectedKeys={[gameData.isSurfingGreenWave ? 'green_wave' : 'whitewater']}
            onChange={(e) => handleWaveTypeChange(e.target.value)}
            className="w-1/2 sm:w-48"
            aria-label="Select wave type"
            classNames={{
              trigger: 'bg-white bg-opacity-90 text-black rounded-lg',
              value: 'text-black',
              listbox: 'bg-white'
            }}
            startContent={
              <SkillShortcut skill={gameData.isSurfingGreenWave ? 'green_wave_riding' : 'whitewater_riding'} />
            }
          >
            <SelectItem key="whitewater" value="whitewater" startContent={<SkillShortcut skill="whitewater_riding" />}>
              White Water
            </SelectItem>
            <SelectItem
              key="green_wave"
              value="green_wave"
              startContent={<SkillShortcut skill="green_wave_riding" />}
              isDisabled={gameData.skills.green_wave_riding?.level < 1}
            >
              Green Wave
            </SelectItem>
          </Select>

          <Checkbox
            isSelected={gameData.catchStoke}
            onValueChange={handleCatchStokeChange}
            color="primary"
            className="w-1/2 text-white bg-white p-2 rounded-lg border border-blue-200"
            size="lg"
            classNames={{
              wrapper: 'mr-2',
              label: 'text-blue-800 ',
              icon: 'text-blue-600'
            }}
          >
            Catch Stoke 🤙
          </Checkbox>
        </div>
      </div>

      {surfSession.sessionInputs && (
        <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4 shadow-lg text-white mt-6">
          <h3 className="text-xl font-semibold mb-2">{currentActivity === 'surf' ? 'Current' : 'Last'} Session</h3>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <Clock className="w-5 h-5 inline-block mr-2 text-gray-200" />
              <span className="text-gray-200">Time:</span>{' '}
              <span className="text-white font-bold">
                {formatDuration(surfSession.elapsedTime)} / {formatDuration(surfSession.totalTime)}
              </span>
            </div>
            <div>
              <Waves className="w-5 h-5 inline-block mr-2 text-gray-200" />
              <span className="text-gray-200">Waves:</span>{' '}
              <span className="text-white font-bold">
                {surfSession.appliedSessionData.filter((wave) => wave.rideResult === 'ride').length}
              </span>
            </div>
            <div>
              <Trophy className="w-5 h-5 inline-block mr-2 text-gray-200" />
              <span className="text-gray-200">Points:</span>{' '}
              <span className="text-white font-bold">
                {formatCurrency(
                  surfSession.appliedSessionData.reduce((sum, wave) => {
                    if (wave.rideResult === 'no_ride' || wave.rideResult === 'wipeout') return sum
                    return sum + Object.values(wave.skills).reduce((skillSum, skill) => skillSum + skill.points, 0)
                  }, 0)
                )}
              </span>
              <span className="text-white font-bold ml-2">
                👏{' '}
                {surfSession.appliedSessionData
                  .reduce((sum, wave) => {
                    if (wave.rideResult === 'no_ride' || wave.rideResult === 'wipeout') return sum
                    return sum + Object.values(wave.skills).reduce((skillSum, skill) => skillSum + skill.respect, 0)
                  }, 0)
                  .toFixed(1)}
              </span>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="flex items-center">
              <Users className="w-5 h-5 mr-2" />
              <span>Crowd: {getStyledConditionText(surfSession.sessionInputs.crowd, CROWD_MAP)}</span>
            </div>
            <div className="flex items-center">
              <Trophy className="w-5 h-5 mr-2" />
              <span>Crowd Skill: {getStyledConditionText(surfSession.sessionInputs.crowdSkill, CROWD_SKILL_MAP)}</span>
            </div>
            <div className="flex items-center">
              <ArrowUp className="w-5 h-5 mr-2" />
              <span>Wave Height: {getStyledConditionText(surfSession.sessionInputs.waveHeight, WAVE_HEIGHT_MAP)}</span>
            </div>
            <div className="flex items-center">
              <Waves className="w-5 h-5 mr-2" />
              <span>Wave Type: {getStyledConditionText(surfSession.sessionInputs.waveType, WAVE_TYPE_MAP)}</span>
            </div>
          </div>

          <div className="space-y-4 mt-6">
            {currentWaves.map((wave) => (
              <WaveCard key={wave.id} wave={wave} waveNumber={wave.id} />
            ))}
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(items) => {
              setItemsPerPage(items)
              setCurrentPage(1)
            }}
            itemsPerPage={itemsPerPage}
          />
        </div>
      )}
    </div>
  )
}

interface WaveCardProps {
  wave: IndexedWaveResult
  waveNumber: number
}

const ResultCard: React.FC<{ result: 'no_ride' | 'wipeout' }> = ({ result }) => {
  return (
    <div className="bg-blue-700 bg-opacity-50 rounded-lg p-2 flex flex-col items-center relative min-w-[100px] pt-4 pb-4">
      <div className="w-8 h-8 flex items-center justify-center">
        {result === 'wipeout' ? <Skull className="w-8 h-8" /> : <X className="w-8 h-8" />}
      </div>
      <span className="text-xs mt-1">{result === 'wipeout' ? 'Wipeout' : 'No ride'}</span>
    </div>
  )
}

const WaveCard: React.FC<WaveCardProps> = ({ wave, waveNumber }) => {
  const { gameData } = useGameplayContext()
  const totalPoints = Number(
    Object.values(wave.skills)
      .reduce((sum, skill) => sum + skill.points, 0)
      .toFixed(1)
  )
  const totalRespectPoints = Object.values(wave.skills).reduce((sum, skill) => sum + skill.respect, 0)
  return (
    <div
      className={`bg-blue-600 bg-opacity-50 rounded-lg p-4 shadow-lg text-white transition-all ${'animate-[slideDown_0.8s_ease-out]'}`}
    >
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-xl font-semibold">Wave {waveNumber}</h3>
        <div className="flex items-center space-x-2 text-sm">
          <div className="flex items-center">
            <Wind className="w-4 h-4 mr-1" />
            {getStyledConditionText(wave.waveInputs.waveSpeed, WAVE_SPEED_MAP)}
          </div>
          <div className="flex items-center">
            <Clock className="w-4 h-4 mr-1" />
            <span
              className="text-white px-2 py-0.5 rounded-full text-sm inline-flex items-center"
              style={{
                backgroundColor: getWaveTimeColor(wave.waveInputs.waveTime),
                textShadow: '0 1px 2px rgba(0,0,0,0.3)'
              }}
            >
              {wave.waveInputs.waveTime.toFixed(1)}s
            </span>
          </div>
          <div className="flex items-center">
            <Timer className="w-4 h-4 mr-1" />
            <span
              className="text-white px-2 py-0.5 rounded-full text-sm inline-flex items-center"
              style={{
                backgroundColor: getWaitingTimeColor(wave.waveInputs.waitingForWaveTime),
                textShadow: '0 1px 2px rgba(0,0,0,0.3)'
              }}
            >
              {formatDuration(wave.waveInputs.waitingForWaveTime)}
            </span>
          </div>
          <div className="flex items-center">
            <CirclePercent className="w-4 h-4 mr-1" />
            <span
              className="text-white px-2 py-0.5 rounded-full text-sm inline-flex items-center"
              style={{
                backgroundColor: getCatchProbabilityColor(wave.waveInputs.catchProbability),
                textShadow: '0 1px 2px rgba(0,0,0,0.3)'
              }}
            >
              {(wave.waveInputs.catchProbability * 100).toFixed(0)}%
            </span>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        {wave.rideResult === 'no_ride' || wave.rideResult === 'wipeout' ? (
          <div /* className={'opacity-0 '} */>
            {/* animate-[slideRight_0.3s_ease-out_0.8s_forwards] */}
            <ResultCard result={wave.rideResult} />
          </div>
        ) : (
          Object.entries(wave.skills).map(([skill, skillResult], index) => (
            <div
              key={skill}
              /*className={'opacity-0'}
               style={{
                animation: 'slideRight 0.3s ease-out forwards',
                animationDelay: `${0.8 + index * 0.1}s`
              }} */
            >
              <SkillResultCard skill={skill as SkillEnum} skillResult={skillResult} gameData={gameData} />
            </div>
          ))
        )}
      </div>
      {wave.hazards.length > 0 && (
        <div className="mt-2">
          <h4 className="font-semibold mb-1">Hazards:</h4>
          {wave.hazards.map((hazard: any, index: number) => (
            <div key={index} className="bg-red-600 bg-opacity-50 rounded-lg p-2 text-sm">
              {formatUnderscoreCase(hazard.severity)} {formatUnderscoreCase(hazard.type)}: Cost:{' '}
              {hazard.consequenceCost} {hazard.consequenceCurrency === 'energy' ? 'energy' : '$'}
            </div>
          ))}
        </div>
      )}
      <div className="flex justify-end items-center mt-2">
        {wave.rideResult === 'wipeout' ? (
          <div className="flex items-center text-red-500 drop-shadow-md">
            <Skull className="w-5 h-5 mr-2" />
            <span className="font-bold text-lg">Wipeout</span>
          </div>
        ) : wave.rideResult === 'no_ride' ? (
          <span className="text-yellow-500">You couldn't catch it</span>
        ) : (
          <>
            <div className="flex items-center">
              <Trophy className="w-5 h-5 mr-2" />
              <span className="font-bold text-lg">{formatCurrency(totalPoints)}</span>
            </div>
            {totalRespectPoints > 0 && (
              <div className="ml-2 flex items-center">
                <span className="mr-1 text-2xl">👏</span>
                <span className="font-bold text-lg">{totalRespectPoints.toFixed(1)}</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

interface SkillResultCardProps {
  skill: SkillEnum
  skillResult: SkillResult
  gameData: GamePlayData
}

const SkillResultCard: React.FC<SkillResultCardProps> = ({ skill, skillResult, gameData }) => {
  return (
    <div className="bg-blue-700 bg-opacity-50 rounded-lg p-2 flex flex-col items-center relative min-w-[100px]">
      {skillResult.isCritical && (
        <div className="absolute -top-4 left-0 right-0 bg-red-500 rounded-t-lg px-1 py-0.5 text-xs text-center whitespace-nowrap">
          🔥 CRITICAL 🔥
        </div>
      )}
      <SkillShortcut skill={skill as SkillEnum} />
      <span className="text-xs mt-1">{formatUnderscoreCase(skill)}</span>
      <div className="flex items-center space-x-2 mt-1">
        <div className="flex items-center">
          <Trophy className="w-3 h-3 mr-1" />
          <span className="text-xs">{skillResult.points.toFixed(1)}</span>
        </div>
        {skillResult.respect > 0 && (
          <div className="flex items-center">
            <span className="text-xs mr-1">👏</span>
            <span className="text-xs">{skillResult.respect.toFixed(1)}</span>
          </div>
        )}
      </div>
    </div>
  )
}

const Pagination: React.FC<{
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  onItemsPerPageChange: (items: number) => void
  itemsPerPage: number
}> = ({ currentPage, totalPages, onPageChange, onItemsPerPageChange, itemsPerPage }) => (
  <div className="flex justify-between items-center my-4">
    <div className="flex space-x-2">
      <button
        onClick={() => onPageChange(1)}
        disabled={currentPage === 1}
        className="px-2 py-1 bg-blue-500 text-white rounded disabled:bg-gray-300"
      >
        <ChevronsLeft size={20} />
      </button>
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="px-2 py-1 bg-blue-500 text-white rounded disabled:bg-gray-300"
      >
        <ChevronLeft size={20} />
      </button>
      <span className="px-2 py-1">
        Page {currentPage} of {totalPages}
      </span>
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="px-2 py-1 bg-blue-500 text-white rounded disabled:bg-gray-300"
      >
        <ChevronRight size={20} />
      </button>
      <button
        onClick={() => onPageChange(totalPages)}
        disabled={currentPage === totalPages}
        className="px-2 py-1 bg-blue-500 text-white rounded disabled:bg-gray-300"
      >
        <ChevronsRight size={20} />
      </button>
    </div>
    <div>
      <select
        value={itemsPerPage}
        onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
        className="px-2 py-1 border rounded text-black"
      >
        <option value={10}>10 per page</option>
        <option value={20}>20 per page</option>
        <option value={50}>50 per page</option>
        <option value={100}>100 per page</option>
      </select>
    </div>
  </div>
)
